'use client';

import { Fragment, useEffect, useState } from 'react';
import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  type RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { cn } from '@/shared/utils/cn';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  enablePagination?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  showVerticalBorder?: boolean;
  renderSubComponent?: (row: TData) => React.ReactNode;
  getRowCanExpand?: (row: TData) => boolean;
  getRowClassName?: (row: TData) => string;
  getRowId?: (row: TData, index: number) => string;
  isLoading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enablePagination = true,
  onRowSelectionChange,
  showVerticalBorder = false,
  renderSubComponent,
  getRowCanExpand,
  getRowClassName,
  getRowId,
  isLoading = false,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getSubRows: (row: TData) => (row as { subRows?: TData[] }).subRows,
    getRowCanExpand: getRowCanExpand
      ? (row) => getRowCanExpand(row.original)
      : (row) => !!(row.subRows && row.subRows.length > 0) || !!renderSubComponent,
    getRowId,
    state: {
      rowSelection,
      expanded,
    },
  });

  // 선택된 행이 변경될 때 부모 컴포넌트에 알림
  useEffect(() => {
    if (onRowSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection]);

  return (
    <div className="flex flex-col rounded-md border overflow-auto bg-white h-full">
      <Table>
        <TableHeader className="bg-gray-100 sticky top-0 z-10 [&_tr]:!border-0 [&_tr:last-child]:!border-b">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    className={cn(
                      showVerticalBorder && index < headerGroup.headers.length - 1 && 'border-r',
                      'px-4',
                      (header.column.columnDef.meta as { className?: string })?.className
                    )}
                    colSpan={header.colSpan}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-white h-full">
          {isLoading
            ? // 로딩 중일 때 스켈레톤 표시
              Array.from({ length: 5 }, (_, i) => `skeleton-row-${i}`).map((rowKey) => (
                <TableRow key={rowKey}>
                  {table.getHeaderGroups()[0]?.headers.map((header) => (
                    <TableCell
                      className={cn(showVerticalBorder && 'border-r', 'px-4')}
                      key={`${rowKey}-${header.id}`}
                    >
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
          {!isLoading && table.getRowModel().rows?.length > 0
            ? table.getRowModel().rows.map((row, rowIndex) => (
                <Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      rowIndex === table.getRowModel().rows.length - 1 &&
                        !row.getIsExpanded() &&
                        '!border-b',
                      getRowClassName?.(row.original)
                    )}
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        className={cn(
                          showVerticalBorder &&
                            index < row.getVisibleCells().length - 1 &&
                            'border-r',
                          'px-4'
                        )}
                        key={cell.id}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && renderSubComponent && (
                    <TableRow
                      className={cn(
                        rowIndex === table.getRowModel().rows.length - 1 && '!border-b'
                      )}
                    >
                      <TableCell colSpan={columns.length}>
                        {renderSubComponent(row.original)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            : null}
          {!isLoading && table.getRowModel().rows?.length === 0 ? (
            <TableRow>
              <TableCell
                className="h-24 text-center"
                colSpan={columns.length}
              >
                데이터가 없습니다.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
